import type { ReactNode } from 'react';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { DEFAULT_SLIDEOUT_WIDTH } from '@/lib/base/overlays/slideout-menu';
import { useSlideoutStackContext } from '@/lib/crm/contexts/SlideoutStackContext';
import { logger } from '@/lib/logger';

import { useSidebarCloseAnimation } from './useSidebarCloseAnimation';

/* eslint-disable react-refresh/only-export-components -- standard context pattern: provider, hook, and context co-located */

const MIN_WIDTH = 400;
const DEFAULT_WIDTH = DEFAULT_SLIDEOUT_WIDTH;
// Allow up to 70% of viewport width (calculated dynamically in setSidebarWidth)

interface ProposalSidebarContextValue {
  // New naming
  openProposalId: number | null;
  openProposal: (id: number, proposalIds?: number[]) => void;
  closeProposal: () => void;
  // Context-aware list of proposal IDs (for auto-advance after approve/reject)
  contextProposalIds: number[];
  // True when a proposal sidebar is on screen — covers the open animation
  // and stays true until close completes. Used by adjacent chat/page layout
  // to coordinate width animations alongside the sidebar.
  isSidebarVisible: boolean;
  // Sidebar width management
  sidebarWidth: number;
  setSidebarWidth: (width: number) => void;
  isResizing: boolean;
  setIsResizing: (resizing: boolean) => void;
  // Backward-compatible aliases (for existing code using Action naming)
  openActionId: number | null;
  openAction: (id: number, proposalIds?: number[]) => void;
  closeAction: () => void;
  // Notification that a proposal was updated (approved/rejected/etc)
  proposalUpdateCounter: number;
  notifyProposalUpdated: () => void;
}

const ProposalSidebarContext = createContext<
  ProposalSidebarContextValue | undefined
>(undefined);

interface ProposalSidebarProviderProps {
  children: ReactNode;
}

/**
 * Loads the persisted sidebar width from localStorage, clamped to viewport.
 * Module-scope helper to keep the provider body focused on logic.
 */
function loadInitialSidebarWidth(): number {
  if (typeof window === 'undefined') {
    return DEFAULT_WIDTH;
  }
  try {
    const saved = localStorage.getItem('ahoy-proposal-sidebar-width');
    const maxWidth = Math.floor(window.innerWidth * 0.7);
    return saved
      ? Math.min(Math.max(parseInt(saved, 10), MIN_WIDTH), maxWidth)
      : DEFAULT_WIDTH;
  } catch (err) {
    logger.warn('Failed to load sidebar width from localStorage', {
      error: err,
      component: 'ActionSidebarContext',
    });
    return DEFAULT_WIDTH;
  }
}

/**
 * Provides proposal-sidebar UI state on top of the unified slideout stack.
 *
 * `openProposalId` is derived from the slideout stack — when the top entry is
 * a `proposal`, that proposal is considered open. `isSidebarVisible` is a
 * higher-level flag that stays true while ANY proposal is in the stack
 * (covered or not) and flips false at the start of a user-initiated close,
 * giving adjacent layout (e.g. ReasoningChat width) time to animate in
 * parallel with the slideout's exit. Width, resize state, the context
 * proposal list, and the proposal-update counter are kept local to this
 * provider because they're specific to the proposal sidebar UI rather than
 * the slideout stack itself.
 *
 * MUST be rendered inside `SlideoutStackProvider`.
 */
export function ProposalSidebarProvider({
  children,
}: ProposalSidebarProviderProps) {
  const { topEntry, stack, openSlideout, clearStack } =
    useSlideoutStackContext();

  // Keep latest stack/topEntry available to deferred callbacks (e.g. the
  // 500ms close timer) without re-creating those callbacks on every stack
  // change. The ref is read inside the timeout to check whether the entry
  // we intended to pop is still the top before popping.
  const stackRef = useRef(stack);
  useEffect(() => {
    stackRef.current = stack;
  }, [stack]);

  // Derive the open proposal ID from the slideout stack. Proposal IDs are
  // integers; we store them as strings in the stack and parse here.
  const openProposalId = useMemo<number | null>(() => {
    if (topEntry?.kind !== 'proposal') {
      return null;
    }
    const parsed = Number(topEntry.id);
    return Number.isFinite(parsed) ? parsed : null;
  }, [topEntry]);

  // True iff there's at least one proposal anywhere in the stack. This is
  // used to keep `isSidebarVisible` aligned with reality when the stack
  // changes for reasons other than openProposal/closeProposal (e.g. browser
  // back/forward toggling the URL).
  const hasProposalInStack = useMemo(
    () => stack.some(e => e.kind === 'proposal'),
    [stack]
  );

  const [contextProposalIds, setContextProposalIds] = useState<number[]>([]);
  // Initialize from the slideout stack so deep-linked URLs (e.g. landing on
  // a page with `?slideoutEntityId=proposal:42`) start with the sidebar
  // already considered visible — otherwise the first paint would treat it
  // as closing.
  const [isSidebarVisible, setIsSidebarVisible] = useState(hasProposalInStack);
  const [sidebarWidth, setSidebarWidthState] = useState(
    loadInitialSidebarWidth
  );
  const [isResizing, setIsResizing] = useState(false);
  const [proposalUpdateCounter, setProposalUpdateCounter] = useState(0);
  const { scheduleOpen, scheduleClose, isClosePending } =
    useSidebarCloseAnimation();

  // Keep `isSidebarVisible` aligned with the slideout stack when it changes
  // for reasons other than `openProposal` / `closeProposal` — primarily
  // browser back/forward navigation, which mutates the URL-synced stack
  // directly. Without the symmetric flip-true case, forward-navigating to
  // restore a proposal would leave `isSidebarVisible` false, so adjacent
  // layout (e.g. ReasoningChat width) wouldn't shrink and the SlideoutMenu
  // would stay closed (it reads `isSidebarVisible` as `isOpen`).
  //
  // Skip the flip-true while a close animation is in flight: `closeProposal`
  // sets visibility false BEFORE popping the stack, so during the 500ms
  // window `hasProposalInStack` is still true and the symmetric branch
  // would otherwise immediately undo the close.
  useEffect(() => {
    if (!hasProposalInStack && isSidebarVisible) {
      setIsSidebarVisible(false);
      setContextProposalIds([]);
    } else if (hasProposalInStack && !isSidebarVisible && !isClosePending()) {
      setIsSidebarVisible(true);
    }
  }, [hasProposalInStack, isSidebarVisible, isClosePending]);

  /**
   * Open a proposal in the sidebar.
   *
   * App-level open (product rule: a click FROM THE APP replaces what's
   * open): the proposal REPLACES the whole slideout stack — including a
   * proposal already on top (sequential transition — no back-navigation
   * between completed and next proposal) and any CRM record slideouts.
   */
  const openProposal = useCallback(
    (id: number, proposalIds?: number[]) => {
      setContextProposalIds(proposalIds ?? []);
      openSlideout({ kind: 'proposal', id: String(id) });
      // Flip visible after paint so the SlideoutMenu's enter animation
      // attaches to the freshly-mounted instance instead of being skipped
      // by an already-true flag. `scheduleOpen` cancels any pending
      // close/open before scheduling the double-rAF visibility flip.
      scheduleOpen(() => setIsSidebarVisible(true));
    },
    [openSlideout, scheduleOpen]
  );

  const closeProposal = useCallback(() => {
    // Capture the proposal entry the user is closing. If something else
    // pushes a new entry during the animation window (e.g. an entity badge
    // click within the proposal, or a URL-driven stack update), the delayed
    // clear must not remove that newer entry.
    const intendedTop = stackRef.current[stackRef.current.length - 1];
    const intendedTopId =
      intendedTop?.kind === 'proposal' ? intendedTop.id : null;
    // First trigger the close animation by flipping visibility — the top
    // ProposalSidebar instance reads this to play its exit animation, and
    // adjacent layout (ReasoningChat width) animates in parallel.
    setIsSidebarVisible(false);
    // After the exit animation, clear the ENTIRE slideout stack — but only
    // if the proposal we intended to close is still on top. `scheduleClose`
    // cancels any pending open rAF and no-ops if a close is already in flight.
    scheduleClose(() => {
      if (intendedTopId === null) {
        return;
      }
      const currentTop = stackRef.current[stackRef.current.length - 1];
      if (currentTop?.kind === 'proposal' && currentTop.id === intendedTopId) {
        clearStack();
      }
    });
  }, [clearStack, scheduleClose]);

  const setSidebarWidth = useCallback((width: number) => {
    // Allow up to 70% of viewport width
    const maxWidth =
      typeof window !== 'undefined'
        ? Math.floor(window.innerWidth * 0.7)
        : 1200;
    const constrainedWidth = Math.min(Math.max(width, MIN_WIDTH), maxWidth);
    setSidebarWidthState(constrainedWidth);
    try {
      localStorage.setItem(
        'ahoy-proposal-sidebar-width',
        constrainedWidth.toString()
      );
    } catch (err) {
      logger.warn('Failed to persist sidebar width', {
        error: err,
        component: 'ActionSidebarContext',
      });
    }
  }, []);

  const notifyProposalUpdated = useCallback(() => {
    setProposalUpdateCounter(c => c + 1);
  }, []);

  const value: ProposalSidebarContextValue = useMemo(
    () => ({
      // New naming
      openProposalId,
      openProposal,
      closeProposal,
      // Context-aware proposal list
      contextProposalIds,
      // Animation coordination
      isSidebarVisible,
      // Sidebar width management
      sidebarWidth,
      setSidebarWidth,
      isResizing,
      setIsResizing,
      // Backward-compatible aliases
      openActionId: openProposalId,
      openAction: openProposal,
      closeAction: closeProposal,
      // Proposal update notification
      proposalUpdateCounter,
      notifyProposalUpdated,
    }),
    [
      openProposalId,
      openProposal,
      closeProposal,
      contextProposalIds,
      isSidebarVisible,
      sidebarWidth,
      setSidebarWidth,
      isResizing,
      proposalUpdateCounter,
      notifyProposalUpdated,
    ]
  );

  return (
    <ProposalSidebarContext value={value}>{children}</ProposalSidebarContext>
  );
}

// Keep backward-compatible exports
export const ActionSidebarProvider = ProposalSidebarProvider;

export function useProposalSidebar(): ProposalSidebarContextValue {
  const context = useContext(ProposalSidebarContext);

  if (context === undefined) {
    throw new Error(
      'useProposalSidebar must be used within a ProposalSidebarProvider'
    );
  }

  return context;
}

/**
 * Optional version of useProposalSidebar that returns undefined if not within a provider.
 * Use this when the component may be rendered outside the provider context.
 */
export function useProposalSidebarOptional():
  | ProposalSidebarContextValue
  | undefined {
  return useContext(ProposalSidebarContext);
}

// Backward-compatible alias
export const useActionSidebar = useProposalSidebar;
